import React, { useState, useEffect, useMemo } from 'react';
import axios_instance from '../api/axios_instance';
import { 
  Loader2, Plus, Image as ImageIcon, 
  X, CheckCircle2, AlertCircle, Layers, 
  Globe, LayoutGrid, Trash2, ArrowLeft
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const AddCategory = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    nameEn: '', nameFr: '', nameAr: '',
    parentCategory: '',
    image: null
  });
  
  const [parents, setParents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });

  // Design Constants (No Shadows)
  const inputStyle = "w-full p-3 border border-gray-300 rounded-xl outline-none bg-white text-sm text-gray-900 focus:border-emerald-500 transition-colors placeholder:text-gray-400 font-medium";
  const labelStyle = "text-[11px] font-black text-gray-500 mb-1.5 block uppercase tracking-wider";

  useEffect(() => {
    const fetchParents = async () => {
      try {
        const res = await axios_instance.get('/api/categories');
        // Only show categories that don't have a parent (Top-level categories)
        setParents(res.data.filter(c => !c.parentCategory));
      } catch (err) { 
        console.error(err); 
      }
    };
    fetchParents();
  }, []);

  const showToast = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 5000);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, image: file });
      setPreview(URL.createObjectURL(file));
    }
  };

  // Validation Logic
  const isFormValid = useMemo(() => {
    return (
      formData.nameAr.trim() !== '' &&
      formData.nameEn.trim() !== '' &&
      formData.nameFr.trim() !== '' &&
      formData.image !== null
    );
  }, [formData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid) return;

    setLoading(true);
    const data = new FormData();
    data.append('name[en]', formData.nameEn);
    data.append('name[fr]', formData.nameFr);
    data.append('name[ar]', formData.nameAr);
    if (formData.parentCategory) data.append('parentCategory', formData.parentCategory);
    data.append('image', formData.image);

    try {
      await axios_instance.post('/api/categories', data);
      showToast("تمت إضافة القسم الجديد بنجاح");
      
      // Reset Form
      setFormData({ nameEn: '', nameFr: '', nameAr: '', parentCategory: '', image: null });
      setPreview(null);
      
      // Optional: Redirect after success
      // setTimeout(() => navigate('/admin/categories'), 2000);
    } catch (err) {
      showToast(err.response?.data?.message || "فشل في إضافة القسم، يرجى المحاولة لاحقاً", "error");
    } finally { 
      setLoading(false); 
    }
  };

  return (
    <div className="p-6  min-h-screen relative" dir="rtl">
      
      {/* Toast Notification */}
      {notification.show && (
        <div className={`fixed top-10 left-1/2 -translate-x-1/2 z-[100] flex items-center gap-3 px-6 py-4 rounded-2xl border animate-in fade-in slide-in-from-top-10 duration-300 ${
          notification.type === 'success' ? 'bg-white border-emerald-100 text-emerald-700' : 'bg-white border-red-100 text-red-700'
        }`}>
          {notification.type === 'success' ? <CheckCircle2 size={20} className="text-emerald-500" /> : <AlertCircle size={20} className="text-red-500" />}
          <span className="text-sm font-bold">{notification.message}</span>
          <button onClick={() => setNotification({ ...notification, show: false })} className="mr-4 text-gray-400 hover:text-gray-600">
            <X size={16} />
          </button>
        </div>
      )}

      <div className="max-w-4xl mx-auto bg-white border border-gray-200 rounded-2xl p-8 relative">
        
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 pb-6 border-b border-gray-100 gap-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <ArrowLeft size={22} className="text-gray-500" />
            </button>
            <div className="p-2 bg-emerald-50 rounded-lg text-emerald-600">
              <LayoutGrid size={22} />
            </div>
            <div>
              <h2 className="text-xl font-black text-gray-800 tracking-tight">إضافة قسم جديد</h2>
              <p className="text-[10px] text-gray-400 font-bold uppercase mt-0.5">Category Management System</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-10">
          
          {/* Multi-language Names */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-gray-400 uppercase tracking-widest text-[10px] font-black mb-4">
              <Globe size={14} /> اللغات والترجمة
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className={labelStyle}>الاسم (AR)</label>
                <input type="text" placeholder="مثال: زيوت طبيعية" value={formData.nameAr} className={inputStyle} onChange={e => setFormData({...formData, nameAr: e.target.value})} required />
              </div>
              <div dir="ltr">
                <label className={labelStyle}>Category Name (EN)</label>
                <input type="text" placeholder="Ex: Natural Oils" value={formData.nameEn} className={inputStyle} onChange={e => setFormData({...formData, nameEn: e.target.value})} required />
              </div>
              <div dir="ltr">
                <label className={labelStyle}>Nom (FR)</label>
                <input type="text" placeholder="Ex: Huiles Naturelles" value={formData.nameFr} className={inputStyle} onChange={e => setFormData({...formData, nameFr: e.target.value})} required />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {/* Category Type */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-gray-400 uppercase tracking-widest text-[10px] font-black mb-4">
                <Layers size={14} /> الهيكل التنظيمي
              </div>
              <div className="bg-gray-50/50 p-6 rounded-2xl border border-gray-100">
                <label className={labelStyle}>تبعية القسم</label>
                <select 
                  className={`${inputStyle} font-bold cursor-pointer appearance-none bg-white`}
                  value={formData.parentCategory}
                  onChange={e => setFormData({...formData, parentCategory: e.target.value})}
                >
                  <option value="">قسم رئيسي (مستوى أول)</option>
                  {parents.map(p => <option key={p._id} value={p._id}>{p.name.ar}</option>)}
                </select>
                <p className="mt-3 text-[10px] text-gray-400 font-medium leading-relaxed">
                  إذا تركت هذا الخيار فارغاً، سيظهر القسم كقسم رئيسي في القائمة العلوية.
                </p>
              </div>
            </div>

            {/* Icon/Image Upload */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-gray-400 uppercase tracking-widest text-[10px] font-black mb-4">
                <ImageIcon size={14} /> الهوية البصرية
              </div>
              <div className="flex items-start gap-4">
                <label className={`relative group cursor-pointer flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-2xl transition-all ${preview ? 'border-emerald-200 bg-emerald-50/30' : 'border-gray-200 bg-gray-50 hover:border-gray-300'}`}>
                  {preview ? (
                    <img src={preview} className="w-full h-full object-contain p-4" alt="preview" />
                  ) : (
                    <>
                      <div className="p-3 bg-white rounded-full border border-gray-100 mb-2">
                        <Plus className="text-gray-400" size={20} />
                      </div>
                      <span className="text-[10px] text-gray-500 font-black uppercase">رفع أيقونة</span>
                    </>
                  )}
                  <input type="file" className="hidden" onChange={handleImageChange} accept="image/*" />
                </label>
                
                {preview && (
                  <button 
                    type="button" 
                    onClick={() => {setPreview(null); setFormData({...formData, image: null})}}
                    className="p-3 bg-red-50 text-red-500 rounded-xl hover:bg-red-100 transition-colors border border-red-100"
                    title="حذف الصورة"
                  >
                    <Trash2 size={20} />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="pt-6">
            <button 
              disabled={loading || !isFormValid}
              type="submit" 
              className={`w-full py-4 rounded-xl font-black text-xs uppercase tracking-widest flex items-center justify-center gap-3 transition-all border ${
                isFormValid && !loading 
                ? 'bg-gray-900 text-white border-gray-900 hover:bg-black' 
                : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
              }`}
            >
              {loading ? <Loader2 className="animate-spin" size={20} /> : <Plus size={20} />}
              {loading ? 'جاري الحفظ...' : isFormValid ? 'تأكيد وحفظ القسم' : 'يرجى إكمال جميع الحقول'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCategory;